import type { SorSettings } from '../settings/general.settings';
import type { CatalogueCode, EmbeddedCode } from './code';
import { TaskEvidenceOptions, TaskGate } from '../tasks/task.evidence';
import { isSorCode } from './code.type';
import { resolveSorEvidenceSnapshot, SOR_EVIDENCE_BUCKETS, SorEvidenceBucket, SorEvidenceOptions, SorEvidenceSettings, sorPolicyToEnforcement } from './sor.evidence';

/** Only the requested buckets carry a flag, and the level map is always present so a caller can read it per bucket. */
export type SorTaskEvidence = Pick<TaskEvidenceOptions, 'beforePhotoEnabled' | 'duringPhotoEnabled' | 'afterPhotoEnabled'> & Required<Pick<TaskEvidenceOptions, 'evidenceEnforcement'>>;

/** The snapshot a line built straight from its catalogue code starts with, as a spread so a non-SOR line gains no key. */
export function resolveSorLineEvidence(code: Pick<CatalogueCode, 'type' | 'sorEvidenceOverride'>, isPending: boolean, sor?: Pick<SorSettings, 'evidence'>): Pick<EmbeddedCode, 'sorEvidence'> {

    return isSorCode(code) ? { sorEvidence: resolveSorEvidenceSnapshot(isPending, sor?.evidence, code.sorEvidenceOverride) } : {};
}

/** Read from settings when the task is created and kept only on the task (D5). */
export interface SorTaskPolicy {
    gate: TaskGate;
}

/** Resolved when the task is created and kept only on the task (D5), so an office edit to the task is never contradicted by a copy on the line. */
export function resolveSorTaskPolicy(sor?: Pick<SorSettings, 'taskGate'>, code?: Pick<CatalogueCode, 'taskGateOverride'>): SorTaskPolicy {

    return { gate: code?.taskGateOverride ?? sor?.taskGate ?? TaskGate.NotEnforced };
}

/** Only a pending SOR line may carry the flag, and only while the tenant allows it; every other line drops it. */
export function permitsWorkAlreadyCompleted(line: Partial<Pick<EmbeddedCode, 'type'>> & { isClaimCode?: boolean }, sor?: Pick<SorSettings, 'allowWorkAlreadyCompletedRequests'>): boolean {

    return isSorCode(line) && !!line.isClaimCode && !!sor?.allowWorkAlreadyCompletedRequests;
}

/** A pending line's task takes the authorised set only when that line records work already done; a legacy flat snapshot reads as Off (D11.4). */
export function selectSorTaskEvidence(evidence: SorEvidenceSettings | undefined, isPending: boolean, workAlreadyCompleted: boolean): SorEvidenceOptions {

    return (isPending && !workAlreadyCompleted ? evidence?.pending : evidence?.authorised) ?? {};
}

/** One mapping for task creation and the approval re-stamp, so a new line and an approved line never read the same policy differently. */
export function sorEvidenceToTaskEvidence(evidence: SorEvidenceOptions, buckets: readonly SorEvidenceBucket[] = SOR_EVIDENCE_BUCKETS): SorTaskEvidence {

    const fields: SorTaskEvidence = { evidenceEnforcement: {} };

    for (const bucket of buckets) {
        const level = sorPolicyToEnforcement(evidence[bucket]);
        fields[`${bucket}Enabled`] = level !== undefined;
        if (level) fields.evidenceEnforcement[bucket] = level;
    }

    return fields;
}
